import { FC, MouseEvent, useEffect, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import UnsavedChangesModal from '../../../common/UnsavedChangesModal/UnsavedChangesModal';
import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';
import { RAW_PROD_TYPE_CODE } from '../../../constants/RawProds';
import { useEditRawProd, useRawProdSourceById } from '../../../services/rawProd/rawProd.service';
import { SubAssembly } from '../../../services/rawProd/rawProd.types';
import { useGenericErrorMessage } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import useNavigateWithToast from '../../../utils/useNavigateWithToast/useNavigateWithToast';
import RawProdForm from '../RawProdForm/RawProdForm';
import { RawProdFlows, RawProdFormErrors, RawProdFormValues } from '../RawProdForm/RawProdForm.types';

const EditRawProd: FC = () => {
  const navigateWithToast = useNavigateWithToast();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const _id = id as string;
  const { data: rawProdData, isError, error } = useRawProdSourceById(_id, true);
  const {
    mutate,
    isError: editRawProdIsError,
    isLoading,
    error: editRawProdError,
  } = useEditRawProd(rawProdData?.data?.moaRawProdNo?.toString());

  const [formValues, setFormValues] = useState<RawProdFormValues>({
    id: undefined,
    mxRef: '',
    moaRawProdNo: undefined,
    rawProdNo: undefined,
    rawProdTpCode: '',
    description: '',
    shortDescription: '',
    statusNo: undefined,
    classNo: undefined,
    printSeq: undefined,
    pcaNo: undefined,
    occasionNo: undefined,
    invenUomNo: undefined,
    prodUomNo: undefined,
    conversionFactor: undefined,
    buyLocal: false,
    accountCode: '',
    newAccountCode: '',
    usageAlgorithm: '',
    frozen: undefined,
    safetyPolicy: undefined,
    glAccountingCode: '',
    glAccountCategoryDescription: '',
  });

  const [initialFormValues, setInitialFormValues] = useState<RawProdFormValues>({
    id: undefined,
    mxRef: '',
    moaRawProdNo: undefined,
    rawProdNo: undefined,
    rawProdTpCode: '',
    description: '',
    shortDescription: '',
    statusNo: undefined,
    classNo: undefined,
    printSeq: undefined,
    pcaNo: undefined,
    occasionNo: undefined,
    invenUomNo: undefined,
    prodUomNo: undefined,
    conversionFactor: undefined,
    buyLocal: false,
    accountCode: '',
    newAccountCode: '',
    usageAlgorithm: '',
    frozen: undefined,
    safetyPolicy: undefined,
    glAccountingCode: '',
    glAccountCategoryDescription: '',
  });

  const [formErrors, setFormErrors] = useState<RawProdFormErrors>({
    mxRef: '',
    moaRawProdNo: '',
    rawProdNo: '',
    rawProdTpCode: '',
    description: '',
    shortDescription: '',
    statusNo: '',
    classNo: '',
    printSeq: '',
    pcaNo: '',
    occasionNo: '',
    invenUomNo: '',
    prodUomNo: '',
    conversionFactor: '',
    buyLocal: '',
    accountCode: '',
    newAccountCode: '',
    usageAlgorithm: '',
    safetyPolicy: '',
    glAccountingCode: '',
    glAccountCategoryDescription: '',
  });

  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [selectedSubassemblyRawProds, setSelectedSubassemblyRawProds] = useState<SubAssembly[]>([]);
  const isSubassembly = rawProdData?.data.rawProdTpCode === RAW_PROD_TYPE_CODE.SubAssembly;

  const checkForUnsavedChanges = () => {
    // only validate subAssemblies if type code is subassembly
    if (isSubassembly) {
      // if subAssemblies from api and selectedSubassembly state length are different, unsaved changes exist
      if (
        rawProdData?.data?.subAssemblies !== undefined &&
        rawProdData?.data?.subAssemblies?.length !== selectedSubassemblyRawProds.length
      ) {
        setUnsavedChanges(true);
        return;
      }
      // deep equal check between api data and selectedSubassemblyRawProds (should also catch usage amount)
      if (
        rawProdData?.data?.subAssemblies !== undefined &&
        !isEqual(rawProdData?.data?.subAssemblies, selectedSubassemblyRawProds)
      ) {
        setUnsavedChanges(true);
        return;
      }
    }
    // check form values against initialFormValues
    if (
      formValues.mxRef !== initialFormValues.mxRef ||
      formValues.moaRawProdNo !== initialFormValues.moaRawProdNo ||
      formValues.rawProdNo !== initialFormValues.rawProdNo ||
      formValues.rawProdTpCode !== initialFormValues.rawProdTpCode ||
      formValues.description !== initialFormValues.description ||
      formValues.shortDescription !== initialFormValues.shortDescription ||
      formValues.statusNo !== initialFormValues.statusNo ||
      formValues.classNo !== initialFormValues.classNo ||
      formValues.printSeq !== initialFormValues.printSeq ||
      formValues.pcaNo !== initialFormValues.pcaNo ||
      formValues.occasionNo !== initialFormValues.occasionNo ||
      formValues.invenUomNo !== initialFormValues.invenUomNo ||
      formValues.prodUomNo !== initialFormValues.prodUomNo ||
      formValues.conversionFactor !== initialFormValues.conversionFactor ||
      formValues.buyLocal !== initialFormValues.buyLocal ||
      formValues.accountCode !== initialFormValues.accountCode ||
      formValues.newAccountCode !== initialFormValues.newAccountCode ||
      formValues.usageAlgorithm !== initialFormValues.usageAlgorithm ||
      formValues.frozen !== initialFormValues.frozen ||
      formValues.safetyPolicy !== initialFormValues.safetyPolicy ||
      formValues.glAccountingCode !== initialFormValues.glAccountingCode ||
      formValues.glAccountCategoryDescription !== initialFormValues.glAccountCategoryDescription
    ) {
      setUnsavedChanges(true);
      return;
    } else {
      setUnsavedChanges(false);
    }
  };

  useEffect(() => {
    checkForUnsavedChanges();
  }, [formValues, selectedSubassemblyRawProds]);

  useGenericErrorMessage([
    { isError, error },
    { isError: editRawProdIsError, error: editRawProdError },
  ]);

  useEffect(() => {
    const rawProdInfo = rawProdData?.data || undefined;
    if (rawProdInfo) {
      const rawProdValues = {
        id: rawProdInfo.id,
        mxRef: rawProdInfo.mxRef,
        moaRawProdNo: rawProdInfo.moaRawProdNo,
        rawProdNo: rawProdInfo.rawProdNo,
        rawProdTpCode: rawProdInfo.rawProdTpCode,
        description: rawProdInfo.description,
        shortDescription: rawProdInfo.shortDescription,
        statusNo: rawProdInfo.statusNo,
        classNo: rawProdInfo.classNo,
        printSeq: rawProdInfo.printSeq,
        pcaNo: rawProdInfo.pcaNo,
        occasionNo: rawProdInfo.occasionNo,
        invenUomNo: rawProdInfo.invenUomNo,
        prodUomNo: rawProdInfo.prodUomNo,
        conversionFactor: rawProdInfo.conversionFactor,
        buyLocal: rawProdInfo.buyLocal,
        accountCode: rawProdInfo.accountCode,
        newAccountCode: rawProdInfo.newAccountCode,
        usageAlgorithm: rawProdInfo.usageAlgorithm,
        frozen: rawProdInfo.frozen,
        safetyPolicy: rawProdInfo.safetyPolicy,
        glAccountingCode: rawProdInfo.glAccountingCode,
        glAccountCategoryDescription: rawProdInfo.glAccountCategoryDescription,
        updatedDate: rawProdInfo.updatedDate,
        updatedBy: rawProdInfo.updatedBy,
      };
      if (rawProdInfo.subAssemblies) setSelectedSubassemblyRawProds(rawProdInfo.subAssemblies);
      setFormValues(rawProdValues);
      setInitialFormValues(rawProdValues);
    }
  }, [rawProdData]);

  const validateFormValues = () => {
    let error = false;
    let errorMessages: RawProdFormErrors = {
      mxRef: '',
      description: '',
      shortDescription: '',
      statusNo: '',
      classNo: '',
      printSeq: '',
      pcaNo: '',
      occasionNo: '',
      invenUomNo: '',
      prodUomNo: '',
      conversionFactor: '',
      buyLocal: '',
      newAccountCode: '',
      accountCode: '',
      usageAlgorithm: '',
      safetyPolicy: '',
    };
    if ([null, undefined, ''].includes(formValues.mxRef)) {
      errorMessages.mxRef = 'MxRef is required.';
      error = true;
    }
    if ([null, undefined, ''].includes(formValues.rawProdTpCode)) {
      errorMessages.rawProdTpCode = 'Raw Product Type Code is required.';
      error = true;
    }
    if (
      [null, undefined, ''].includes(formValues.description) ||
      formValues.description.length < 1 ||
      formValues.description.length > 50
    ) {
      errorMessages.description = 'Description must be 1-50 characters.';
      error = true;
    }
    if (
      [null, undefined, ''].includes(formValues.shortDescription) ||
      formValues.shortDescription.length < 1 ||
      formValues.shortDescription.length > 16
    ) {
      errorMessages.shortDescription = 'Short Description must be 1-16 characters.';
      error = true;
    }
    if ([null, undefined, -1].includes(formValues.statusNo)) {
      errorMessages.statusNo = 'Status is required.';
      error = true;
    }
    if ([null, undefined, -1].includes(formValues.classNo)) {
      errorMessages.classNo = 'Class is required.';
      error = true;
    }
    if ([null, undefined, -1, ''].includes(formValues.printSeq)) {
      errorMessages.printSeq = 'Print seq is required.';
      error = true;
    }
    if ([null, undefined, -1].includes(formValues.pcaNo)) {
      errorMessages.pcaNo = 'PCA Number is required.';
      error = true;
    }
    if ([null, undefined, -1].includes(formValues.occasionNo)) {
      errorMessages.occasionNo = 'Occasion is required.';
      error = true;
    }
    if ([null, undefined, -1].includes(formValues.invenUomNo)) {
      errorMessages.invenUomNo = 'Inventory UOM is required.';
      error = true;
    }
    if ([null, undefined, -1].includes(formValues.prodUomNo)) {
      errorMessages.prodUomNo = 'Prod UOM is required.';
      error = true;
    }
    if ([null, undefined, -1, ''].includes(formValues.conversionFactor)) {
      errorMessages.conversionFactor = 'Conversion Factor is required.';
      error = true;
    }
    if (formValues.buyLocal === undefined) {
      errorMessages.buyLocal = 'Buy Local is required.';
      error = true;
    }
    if ([null, undefined, ''].includes(formValues.newAccountCode)) {
      errorMessages.newAccountCode = 'New Account Code is required.';
      error = true;
    }
    if ([null, undefined, ''].includes(formValues.accountCode)) {
      errorMessages.accountCode = 'Account Code is required.';
      error = true;
    }
    if ([null, undefined, ''].includes(formValues.usageAlgorithm)) {
      errorMessages.usageAlgorithm = 'Usage Algorithm is required.';
      error = true;
    }
    if ([null, undefined, -1, ''].includes(formValues.safetyPolicy)) {
      errorMessages.safetyPolicy = 'Safety is required.';
      error = true;
    }

    setFormErrors((formErrors) => {
      return { ...formErrors, ...errorMessages };
    });
    return error;
  };

  const handleSave = (event: MouseEvent) => {
    event.preventDefault();
    const isError = validateFormValues();
    if (isSubassembly && selectedSubassemblyRawProds.length === 0) {
      showToast({
        label: 'Subassembly requires at least one subassembly raw product to be assigned.',
        subtext: 'Please add a subassembly raw product record.',
        id: 'noSubassemblyRawProds',
        toastType: ToastTypes.Error,
      });
      return;
    }
    if (isError) return;
    setUnsavedChanges(false);
    mutate(
      {
        id: formValues.id as number,
        mxRef: formValues.mxRef,
        moaRawProdNo: formValues.moaRawProdNo,
        rawProdNo: formValues.rawProdNo,
        rawProdTpCode: formValues.rawProdTpCode,
        description: formValues?.description,
        shortDescription: formValues?.shortDescription,
        statusNo: formValues?.statusNo as number,
        classNo: formValues?.classNo as number,
        printSeq: formValues?.printSeq as number,
        pcaNo: formValues?.pcaNo as number,
        occasionNo: formValues?.occasionNo as number,
        invenUomNo: formValues?.invenUomNo as number,
        prodUomNo: formValues?.prodUomNo as number,
        conversionFactor: formValues?.conversionFactor as number,
        buyLocal: formValues?.buyLocal,
        accountCode: formValues?.accountCode,
        newAccountCode: formValues?.newAccountCode,
        usageAlgorithm: formValues?.usageAlgorithm,
        frozen: formValues?.frozen,
        safetyPolicy: formValues?.safetyPolicy as number,
        glAccountingCode: formValues.glAccountingCode,
        glAccountCategoryDescription: formValues.glAccountCategoryDescription,
        subAssemblies: selectedSubassemblyRawProds,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('rawprod');
          navigateWithToast('/tables/rawproducts', {
            label: 'Raw product has been updated successfully.',
            id: 'rawProdUpdateSuccess',
          });
        },
      }
    );
  };

  return (
    <>
      <RawProdForm
        formValues={formValues}
        formErrors={formErrors}
        setFormValues={setFormValues}
        setFormErrors={setFormErrors}
        selectedSubassemblyRawProds={selectedSubassemblyRawProds}
        setSelectedSubassemblyRawProds={setSelectedSubassemblyRawProds}
        saveDisabled={!unsavedChanges}
        flow={RawProdFlows.edit}
        handleSave={handleSave}
        isButtonLoading={isLoading}
        headerTitle={
          rawProdData?.data
            ? `Raw Product ${rawProdData?.data?.moaRawProdNo?.toString()} - ${rawProdData?.data?.mxRef}`
            : 'Raw Product'
        }
        disabledInputs={['mxRef', 'mmsRawProdNo', 'rawProdNo', 'rawProdTpCode', 'updatedBy', 'updatedDate']}
      />
      <UnsavedChangesModal unsavedChangesExist={unsavedChanges} />
    </>
  );
};

export default EditRawProd;
